import React, { useState } from 'react';
import Button from 'components/styleguide/button/Button';
import DocumentUploader, { DefaultDocument } from 'components/styleguide/document_uploader/DocumentUploader';
import { getCSRFToken } from 'lib/request';

interface ResumeState {
  uploadedCv?: DefaultDocument;
  selectedCv?: DefaultDocument;
  profileCvs: DefaultDocument[];
}

interface ResumeProps {
  jobId: string;
  isUserAuthenticated: boolean;
  initialSelectedCv?: DefaultDocument | null;
  initialProfileCvs?: DefaultDocument[] | null;
  initialUploadedCv?: DefaultDocument | null;
  nextUrl: string;
  previousUrl?: string | null;
  isLastStep?: boolean;
}

const sortByUploadDate = (cvs: DefaultDocument[]): DefaultDocument[] =>
  [...cvs].sort((a, b) =>
    new Date(b.uploaded_at || 0).getTime() - new Date(a.uploaded_at || 0).getTime()
  );

const Resume: React.FC<ResumeProps> = ({
  jobId,
  isUserAuthenticated,
  initialSelectedCv,
  initialProfileCvs,
  initialUploadedCv,
  nextUrl,
  previousUrl,
  isLastStep,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [resumeState, setResumeState] = useState<ResumeState>({
    selectedCv: initialSelectedCv || undefined,
    profileCvs: initialProfileCvs || [],
    uploadedCv: initialUploadedCv || undefined,
  });

  const { uploadedCv, selectedCv, profileCvs } = resumeState;
  const isBusy = isUploading || isUpdating;
  const hasResume = Boolean(selectedCv) || (!isUserAuthenticated && Boolean(uploadedCv));
  const sortedProfileCvs = sortByUploadDate(profileCvs);
  const isUploadedCvSelected = Boolean(uploadedCv) && selectedCv?.id === uploadedCv?.id;
  const showLandingProfileHint = !isUserAuthenticated && !selectedCv && Boolean(uploadedCv);

  const handleResumeSelect = (cv: DefaultDocument) => {
    setResumeState(prev => ({ ...prev, selectedCv: cv }));
  };

  const handleResumeRemove = (cv: DefaultDocument) => {
    setResumeState(prev => ({
      ...prev,
      profileCvs: prev.profileCvs.filter(c => c.id !== cv.id),
      selectedCv: cv.id === prev.selectedCv?.id ? undefined : prev.selectedCv,
    }));
  };

  const submitSelectedCv = async () => {
    setIsUpdating(true);
    try {
      const response = await fetch(window.Routes.update_cv_job_ad_quick_applications_url(jobId), {
        method: 'PATCH',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken() || '',
        },
        body: JSON.stringify({ cv_id: selectedCv?.id }),
      });
      const data = await response.json();
      if (data.success) window.location.href = nextUrl;
    } catch {
      // Silently handle error
    } finally {
      setIsUpdating(false);
    }
  };

  const handleNext = () => {
    if (isBusy || !hasResume) return;
    if (!isUserAuthenticated) {
      window.location.href = nextUrl;
      return;
    }
    submitSelectedCv();
  };

  const handleFileSelect = async (file: File) => {
    setUploadError('');
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('cv[file]', file, file.name);

      const response = await fetch(window.Routes.upload_cv_job_ad_quick_applications_url(jobId), {
        method: 'POST',
        mode: 'cors',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': getCSRFToken() || '',
        },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message);

      if (isUserAuthenticated) {
        const uploaded = data.profile_cvs?.find((cv: DefaultDocument) => cv.id === data.selected_cv_id);
        if (!uploaded) return;

        setResumeState(prev => ({
          ...prev,
          uploadedCv: { ...uploaded, subtitle: 'Uploaded now' },
          profileCvs: data.profile_cvs?.filter((c: DefaultDocument) => c.id !== uploaded.id) || [],
        }));
      } else {
        const uploaded = { ...data.selected_cv, subtitle: 'Uploaded now' };
        setResumeState(prev => ({
          ...prev,
          uploadedCv: uploaded,
          selectedCv: uploaded,
        }));
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsUploading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== 'Go') return;
    e.preventDefault();
    if (isBusy) return;

    if (hasResume) {
      handleNext();
    } else if (!uploadedCv && sortedProfileCvs.length === 0) {
      (document.querySelector('#resume') as HTMLInputElement | null)?.click();
    }
  };

  return (
    <>
      <div className="quick-apply-step__documents-list" onKeyDown={handleKeyDown}>
        <DocumentUploader
          id="resume"
          title="Click to upload"
          subtitle="PDF files only. Maximum file size of 10MB."
          loadingTitle="Uploading your resume..."
          hint={showLandingProfileHint ? 'Uploaded from your Landing profile' : ''}
          error={uploadError}
          accept=".pdf"
          required={false}
          isMultiple={isUserAuthenticated}
          uploadedDocument={uploadedCv}
          isDocumentSelected={isUploadedCvSelected}
          isLoading={isUploading}
          onFileUpload={handleFileSelect}
          onDocumentSelect={handleResumeSelect}
        />
        {isUserAuthenticated && sortedProfileCvs.map((doc) => (
          <DocumentUploader
            key={doc.id}
            id={`resume-${doc.id}`}
            isMultiple
            uploadedDocument={{
              ...doc,
              subtitle: doc.uploaded_at ? `Uploaded on ${doc.uploaded_at}` : '',
            }}
            isDocumentSelected={selectedCv?.id === doc.id}
            onDocumentSelect={() => handleResumeSelect(doc)}
            previewUrl={window.Routes?.resume_url?.(doc.id)}
            deleteUrl={window.Routes?.resume_url?.(doc.id)}
            onDelete={() => handleResumeRemove(doc)}
          />
        ))}

        <p className="quick-apply-step__privacy-notice">
          We use Antrophic to safely and automatically update your profile. Check our{' '}
          <a href="/privacy" target="_blank" className="quick-apply-step__link">Privacy policy</a> to know more.
        </p>
      </div>

      <div className="quick-apply-step__footer">
        {previousUrl && (
          <Button
            href={previousUrl}
            type="secondary"
            context="talent"
            iconLeft="arrowLeft"
            fullWidth
          >
            Previous
          </Button>
        )}

        <Button
          onClick={handleNext}
          disabled={isBusy || !hasResume}
          context="talent"
          iconRight={isLastStep ? undefined : 'arrowRight'}
          fullWidth
        >
          {isLastStep ? 'Apply' : 'Next'}
        </Button>
      </div>
    </>
  );
};

export default Resume;
